import { useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { ReimbursementLink } from '@/api/reimbursements';
import { HelpIcon } from '@/components/icons';
import { MoneyText } from '@/components/MoneyText';
import { MatchPill } from '@/features/accounts/MatchPill';
import { ReimbursementTargetPicker } from '@/features/accounts/ReimbursementTargetPicker';
import { matchWords } from '@/features/accounts/reimbursementMatch';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { resolveTokens } from '@/theme/theme';
import { PaybacksHelpDialog } from './PaybacksHelpDialog';
import {
  hidePaybackForLater,
  showPaybacksHiddenForLater,
  usePaybacksHiddenForLater,
} from './paybackLater';

/** One side of a payback suggestion, built from its line (TMMAPP-0121 item 5), never a string. */
export interface PaybackSide {
  accountId: number;
  accountName: string;
  postedOn: string;
  /** The bank amount, in the line's (the account's) currency. */
  amountMinor: number;
  currency: string | null;
  operatingAmountMinor: number;
  /** The served payee's name, else the bank's text. */
  payee: string;
  /** True when `payee` is the bank's text (no served payee): it clamps, with the text in `title`. */
  bankText: boolean;
  /** The envelopes the line is allocated to, in order. */
  envelopes: string[];
  /** The line is classified as a transfer: the pair is never listed (item 4). */
  isTransfer: boolean;
}

export interface PaybackPair {
  link: ReimbursementLink;
  /** Null when the side failed to load: it reads "Transaction unavailable". */
  credit: PaybackSide | null;
  expense: PaybackSide | null;
}

/**
 * The match chip's words (item 3), from the link's served reason (`matchWords`). NEAR's difference:
 * both sides in one currency compare their bank amounts in it; sides in two currencies compare
 * operating amounts.
 */
function matchLabel(
  t: TFunction,
  link: ReimbursementLink,
  credit: PaybackSide,
  expense: PaybackSide,
  operatingCurrency: string | null,
  locale: string,
): string {
  return matchWords(t, link, () => {
    const sameCurrency = credit.currency === expense.currency;
    const creditMinor = Math.abs(sameCurrency ? credit.amountMinor : credit.operatingAmountMinor);
    const expenseMinor = Math.abs(
      sameCurrency ? expense.amountMinor : expense.operatingAmountMinor,
    );
    return formatMoneyMinor(
      Math.abs(expenseMinor - creditMinor),
      sameCurrency ? credit.currency : operatingCurrency,
      locale,
    );
  });
}

/** The bank's text on one line, the full text in `title`; a served name is shown as it is. */
const CLAMP_SX = {
  display: 'inline-block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'bottom',
} as const;

function SidePanel({
  eyebrow,
  side,
  extra,
  action,
  onAction,
  describedBy,
  tone,
}: {
  eyebrow: string;
  side: PaybackSide | null;
  /** The account name (money in) or the envelope (purchase), after date · payee. */
  extra: string | null;
  action: { label: string; name: PairAction } | null;
  onAction: (event: MouseEvent<HTMLButtonElement>) => void;
  describedBy: string | undefined;
  tone: 'in' | 'out';
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  return (
    <Stack
      data-payback-side={tone}
      spacing={0.5}
      sx={{
        minWidth: 0,
        p: 1.5,
        bgcolor: tokens.surface,
        // The money in carries the success tone as its border: `moneyPositive`, `text`, `text2` and
        // `link` on `successSoft` aren't guaranteed pairs, so the panel stays on `surface` (item 2).
        border: `1px solid ${tone === 'in' ? tokens.successFg : tokens.line}`,
        borderRadius: `${tokens.radius.control}px`,
      }}
    >
      <Typography variant="eyebrow" component="p">
        {eyebrow}
      </Typography>
      {side === null ? (
        <Typography variant="body2" sx={{ color: tokens.text2 }}>
          {t('reimb.unavailable')}
        </Typography>
      ) : (
        <>
          <MoneyText
            minor={side.amountMinor}
            currency={side.currency}
            variant="figureSm"
            component="div"
            tone="signed"
          />
          <Typography variant="body2" sx={{ color: tokens.text2, overflowWrap: 'anywhere' }}>
            {formatCivilDate(side.postedOn, locale)}
            {' · '}
            <Box
              component="span"
              data-payback-payee=""
              title={side.bankText ? side.payee : undefined}
              sx={{ color: tokens.text, ...(side.bankText ? CLAMP_SX : {}) }}
            >
              {side.payee}
            </Box>
            {extra !== null && ` · ${extra}`}
          </Typography>
        </>
      )}
      {action !== null && (
        <Box sx={{ pt: 0.5, mt: 'auto' }}>
          <Button
            variant="text"
            size="small"
            data-payback-action={action.name}
            aria-describedby={describedBy}
            onClick={onAction}
            sx={{ textAlign: 'start', marginInlineStart: -1 }}
          >
            {action.label}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

function envelopeText(side: PaybackSide | null): string | null {
  if (side === null || side.envelopes.length === 0) return null;
  const [first, ...rest] = side.envelopes;
  return rest.length === 0 ? first! : `${first} +${rest.length}`;
}

type PairAction = 'yes' | 'no' | 'later' | 'transfer' | 'another';

interface PairHandlers {
  /** Resolves true once the decision is saved (and the queue reloaded), false if it was refused. */
  onYes: (link: ReimbursementLink) => Promise<boolean>;
  onNo: (link: ReimbursementLink) => Promise<boolean>;
  onMarkTransfer: (link: ReimbursementLink) => void;
  onPickAnother: (link: ReimbursementLink) => void;
  /**
   * Resends Yes with the envelope the purchase came from, after Yes was refused for want of one
   * (`REIMBURSEMENT_TARGET_REQUIRED`, TMMAPP-0140 item 3). Resolves as `onYes` does.
   */
  onPickEnvelope: (link: ReimbursementLink, envelopeId: number) => Promise<boolean>;
  /** Puts the envelope question away: the link stays as it was. */
  onCancelEnvelope: (link: ReimbursementLink) => void;
}

/**
 * One suggestion as a pair card (TMMAPP-0121 item 2): money in · the match and the three stacked
 * actions · the earlier purchase at `md`+, and money in, purchase, then the actions below. The DOM
 * order is the visual order at each width. Every action goes through `useSubscriberAction()`: a
 * person without a subscription sees the same controls and opens the purchase experience.
 */
function PaybackPairCard({
  pair,
  pageAccountId,
  operatingCurrency,
  handlers,
  asksEnvelope,
  onLeaving,
  onStayed,
}: {
  pair: PaybackPair;
  pageAccountId: number | undefined;
  operatingCurrency: string | null;
  handlers: PairHandlers;
  /** Yes was refused for want of an envelope: the card asks which one, under its panels. */
  asksEnvelope: boolean;
  /** The pressed action takes the card away: focus moves on once it has gone (item 13). */
  onLeaving: () => void;
  /** A Yes / No was refused: the card stays, and so does focus. */
  onStayed: () => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const { link, credit, expense } = pair;
  // Yes and No send one request at a time: a double click sends one (TMMAPP-0122 item 13).
  const deciding = useRef(false);
  const [resending, setResending] = useState(false);
  const yesRef = useRef<HTMLButtonElement>(null);
  const decide = async (write: (link: ReimbursementLink) => Promise<boolean>) => {
    if (deciding.current) return;
    deciding.current = true;
    onLeaving();
    try {
      if (!(await write(link))) onStayed();
    } finally {
      deciding.current = false;
    }
  };
  const pickEnvelope = async (envelopeId: number) => {
    setResending(true);
    try {
      await decide((target) => handlers.onPickEnvelope(target, envelopeId));
    } finally {
      setResending(false);
    }
  };
  // One gate for the card's five actions: the button names its action in `data-payback-action`.
  const gate = useSubscriberAction((event) => {
    const action = event.currentTarget.dataset.paybackAction as PairAction | undefined;
    if (action === 'yes') void decide(handlers.onYes);
    else if (action === 'no') void decide(handlers.onNo);
    else if (action === 'later') {
      onLeaving();
      hidePaybackForLater(link.id);
    } else if (action === 'transfer') handlers.onMarkTransfer(link);
    else if (action === 'another') handlers.onPickAnother(link);
  });
  const loaded = credit !== null && expense !== null;
  const describedBy = gate.descriptionId;

  const moneyIn = (
    <SidePanel
      key="in"
      tone="in"
      eyebrow={t('reimb.moneyIn')}
      side={credit}
      extra={credit !== null && credit.accountId !== pageAccountId ? credit.accountName : null}
      action={loaded ? { label: t('reimb.markTransfer'), name: 'transfer' } : null}
      onAction={gate.run}
      describedBy={describedBy}
    />
  );
  const purchase = (
    <SidePanel
      key="out"
      tone="out"
      eyebrow={t('reimb.earlierPurchase')}
      side={expense}
      extra={envelopeText(expense)}
      action={loaded ? { label: t('reimb.pickAnother'), name: 'another' } : null}
      onAction={gate.run}
      describedBy={describedBy}
    />
  );
  const middle = (
    <Stack
      key="middle"
      data-payback-actions=""
      spacing={1}
      sx={{ alignItems: 'stretch', justifyContent: 'center', minWidth: 0 }}
    >
      {loaded && (
        <Box sx={{ textAlign: 'center' }}>
          {/* A link the person made is neutral: it's no match grade (item 24). */}
          <MatchPill
            match={link}
            label={matchLabel(t, link, credit, expense, operatingCurrency, locale)}
          />
        </Box>
      )}
      {loaded && (
        <>
          <Button
            size="small"
            fullWidth
            variant={link.grade === 'NEAR' ? 'outlined' : 'contained'}
            aria-describedby={describedBy}
            data-payback-action="yes"
            ref={yesRef}
            onClick={gate.run}
          >
            {t('reimb.yes')}
          </Button>
          <Button
            size="small"
            fullWidth
            variant="outlined"
            aria-describedby={describedBy}
            data-payback-action="no"
            onClick={gate.run}
          >
            {t('reimb.no')}
          </Button>
        </>
      )}
      <Button
        size="small"
        fullWidth
        variant="text"
        aria-describedby={describedBy}
        data-payback-action="later"
        onClick={gate.run}
      >
        {t('reimb.later')}
      </Button>
      <SubscriberActionHint id={gate.descriptionId} description={gate.description} />
    </Stack>
  );

  return (
    <Box
      role="group"
      aria-label={t('reimb.pairGroup', {
        credit: credit?.payee ?? t('reimb.unavailable'),
        expense: expense?.payee ?? t('reimb.unavailable'),
      })}
      data-payback-pair={link.id}
      tabIndex={-1}
      sx={{
        display: 'grid',
        gridTemplateColumns: wide ? 'minmax(0,1fr) 180px minmax(0,1fr)' : 'minmax(0,1fr)',
        gap: 2,
        p: 2,
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: `${tokens.radius.card}px`,
        boxShadow: tokens.elevation.card,
      }}
    >
      {wide ? [moneyIn, middle, purchase] : [moneyIn, purchase, middle]}
      {asksEnvelope && (
        <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <ReimbursementTargetPicker
            busy={resending}
            onConfirm={(envelopeId) => void pickEnvelope(envelopeId)}
            onCancel={() => {
              // The question unmounts with focus in it: Yes, which asked it, takes focus back.
              handlers.onCancelEnvelope(link);
              yesRef.current?.focus();
            }}
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * "Did someone pay you back?" (TMMAPP-0121 §A): the heading row with "Explain this", one pair card
 * per suggestion, and "{n} hidden for later · Show". No reason prose, confidence chip or AI pill: the
 * matches are arithmetic (MM-D-95, TMM-B3, TMM-B9).
 */
export function PaybackPairs({
  pairs,
  pageAccountId,
  operatingCurrency,
  handlers,
  envelopeLinkId = null,
}: {
  pairs: PaybackPair[];
  pageAccountId: number | undefined;
  operatingCurrency: string | null;
  handlers: PairHandlers;
  /** The link whose Yes needs an envelope (TMMAPP-0140 item 3); its card asks for one. */
  envelopeLinkId?: number | null;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const hidden = usePaybacksHiddenForLater();
  const [helpOpen, setHelpOpen] = useState(false);
  const visible = pairs.filter((pair) => !hidden.has(pair.link.id));
  const hiddenCount = pairs.length - visible.length;
  const listRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Later, Show, Yes and No unmount the pressed button (TMMAPP-0122 item 13). `leave`: once that pair
  // has gone, focus the pair now in its place, else the heading. `reveal`: focus the first pair Show
  // brought back.
  const pendingFocus = useRef<
    { kind: 'leave'; id: number; index: number } | { kind: 'reveal'; id: number } | null
  >(null);
  const visibleKey = visible.map((pair) => pair.link.id).join(',');
  useLayoutEffect(() => {
    const pending = pendingFocus.current;
    if (pending === null) return;
    const ids = visibleKey === '' ? [] : visibleKey.split(',').map(Number);
    const cards = listRef.current?.querySelectorAll<HTMLElement>('[data-payback-pair]');
    if (pending.kind === 'reveal') {
      if (!ids.includes(pending.id)) return;
      pendingFocus.current = null;
      cards?.[ids.indexOf(pending.id)]?.focus();
      return;
    }
    if (ids.includes(pending.id)) return;
    pendingFocus.current = null;
    (cards?.[pending.index] ?? headingRef.current)?.focus();
  }, [visibleKey]);

  const showHidden = () => {
    const first = pairs.find((pair) => hidden.has(pair.link.id));
    if (first !== undefined) pendingFocus.current = { kind: 'reveal', id: first.link.id };
    showPaybacksHiddenForLater();
  };

  return (
    <Stack spacing={1.5} data-paybacks="" ref={listRef}>
      <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 16rem', minWidth: 0 }}>
          <Typography variant="h3" ref={headingRef} tabIndex={-1}>
            {t('reimb.queueTitle')}
          </Typography>
          <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
            {t('reimb.queueSub')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<HelpIcon />}
          onClick={() => setHelpOpen(true)}
        >
          {t('reimb.explain')}
        </Button>
      </Stack>
      {visible.map((pair, index) => (
        <PaybackPairCard
          key={pair.link.id}
          pair={pair}
          pageAccountId={pageAccountId}
          operatingCurrency={operatingCurrency}
          handlers={handlers}
          asksEnvelope={pair.link.id === envelopeLinkId}
          onLeaving={() => {
            pendingFocus.current = { kind: 'leave', id: pair.link.id, index };
          }}
          onStayed={() => {
            pendingFocus.current = null;
          }}
        />
      ))}
      {hiddenCount > 0 && (
        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: tokens.text2 }}>
            {t('reimb.hiddenForLater', { count: hiddenCount })}
            {' · '}
          </Typography>
          <Button variant="text" size="small" onClick={showHidden}>
            {t('reimb.showHidden')}
          </Button>
        </Stack>
      )}
      {helpOpen && <PaybacksHelpDialog onClose={() => setHelpOpen(false)} />}
    </Stack>
  );
}
